<!-- src/views/ReportesGymView.vue -->
<template>
  <div class="p-2 sm:p-4 md:p-6 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Encabezado Principal -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-emerald-600 dark:text-emerald-400 tracking-widest uppercase">Módulo de Gimnasio</span>
        </div>
        <h1 class="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2.5">
          <Icon icon="mdi:chart-box-outline" class="text-emerald-600 dark:text-emerald-400 text-2xl sm:text-3xl" />
          Reportes del Gimnasio
        </h1>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Auditoría de Pagos por Método (Crédito Cashea, Pago Móvil, Efectivo) y Control Histórico de Accesos
        </p>
      </div>

      <!-- Selector de Pestañas (Tabs) -->
      <div class="flex items-center bg-gray-100 dark:bg-gray-700/60 p-1.5 rounded-2xl w-full md:w-auto border border-gray-200 dark:border-gray-600">
        <button
          @click="tabActiva = 'pagos'"
          :class="[
            'flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-black transition-all cursor-pointer',
            tabActiva === 'pagos'
              ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
              : 'text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400'
          ]"
        >
          <Icon icon="mdi:cash-register" class="text-base sm:text-lg" />
          <span>Pagos y Métodos</span>
          <span v-if="pagosList.length > 0" class="text-[10px] px-1.5 py-0.2 rounded-full font-bold"
            :class="tabActiva === 'pagos' ? 'bg-emerald-800 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'">
            {{ pagosList.length }}
          </span>
        </button>

        <button
          @click="tabActiva = 'accesos'"
          :class="[
            'flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-black transition-all cursor-pointer',
            tabActiva === 'accesos'
              ? 'bg-cyan-600 text-white shadow-md shadow-cyan-500/20'
              : 'text-gray-600 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400'
          ]"
        >
          <Icon icon="mdi:door-open" class="text-base sm:text-lg" />
          <span>Asistencias y Accesos</span>
          <span v-if="accesosList.length > 0" class="text-[10px] px-1.5 py-0.2 rounded-full font-bold"
            :class="tabActiva === 'accesos' ? 'bg-cyan-800 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'">
            {{ accesosList.length }}
          </span>
        </button>
      </div>
    </div>

    <!-- ==================================================================== -->
    <!-- TAB 1: REPORTE DE PAGOS POR MÉTODO (CASHEA, PAGO MÓVIL, EFECTIVO)     -->
    <!-- ==================================================================== -->
    <div v-show="tabActiva === 'pagos'" class="space-y-5 animate-fadeIn">
      <!-- TARJETAS KPI DE RECAUDACIÓN POR MÉTODO -->
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
        <!-- Total Recaudado USD -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-emerald-200 dark:border-emerald-900/50">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Total Recaudado</span>
            <div class="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600">
              <Icon icon="mdi:currency-usd" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">${{ formatMonto(resumenPagos.totalRecaudadoUsd) }}</p>
          <p class="text-[10px] sm:text-[11px] font-bold text-emerald-600 dark:text-emerald-400 mt-0.5 font-mono">
            Bs. {{ formatBs(resumenPagos.totalRecaudadoBs) }}
          </p>
          <span class="text-[9px] text-gray-400 mt-1 block">{{ resumenPagos.totalTransacciones || 0 }} pagos registrados</span>
        </div>

        <!-- Crédito Cashea (DESTACADO) -->
        <div class="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-yellow-900/20 dark:to-amber-900/10 rounded-2xl p-3 sm:p-4 shadow-sm border-2 border-yellow-400 dark:border-yellow-600">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-yellow-800 dark:text-yellow-400 flex items-center gap-1">
              <Icon icon="mdi:flash" class="text-yellow-600 text-sm" /> Cashea
            </span>
            <span class="px-1.5 py-0.5 rounded text-[8px] font-black bg-yellow-400 text-yellow-950 uppercase">Crédito</span>
          </div>
          <p class="text-lg sm:text-2xl font-black text-yellow-900 dark:text-yellow-200">${{ formatMonto(resumenPagos.totalCasheaUsd) }}</p>
          <p class="text-[10px] sm:text-[11px] font-bold text-yellow-700 dark:text-yellow-400 mt-0.5 font-mono">
            Bs. {{ formatBs(resumenPagos.totalCasheaBs) }}
          </p>
          <span class="text-[9px] text-yellow-800/80 dark:text-yellow-400/80 mt-1 block font-bold">
            {{ resumenPagos.cantidadCashea || 0 }} pagos Cashea
          </span>
        </div>

        <!-- Pago Móvil -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-blue-200 dark:border-blue-900/50">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-blue-600 dark:text-blue-400">Pago Móvil</span>
            <div class="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600">
              <Icon icon="mdi:cellphone-wireless" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">${{ formatMonto(resumenPagos.totalPagoMovilUsd) }}</p>
          <p class="text-[10px] sm:text-[11px] font-bold text-blue-600 dark:text-blue-400 mt-0.5 font-mono">
            Bs. {{ formatBs(resumenPagos.totalPagoMovilBs) }}
          </p>
          <span class="text-[9px] text-gray-400 mt-1 block">{{ resumenPagos.cantidadPagoMovil || 0 }} transacciones</span>
        </div>

        <!-- Efectivo USD / Bs -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-green-200 dark:border-green-900/50">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-green-600 dark:text-green-400">Efectivo</span>
            <div class="p-1.5 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600">
              <Icon icon="mdi:cash-multiple" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">${{ formatMonto(resumenPagos.totalEfectivoUsd) }}</p>
          <p class="text-[10px] sm:text-[11px] font-bold text-green-600 dark:text-green-400 mt-0.5 font-mono">
            Bs. {{ formatBs(resumenPagos.totalEfectivoBs) }}
          </p>
          <span class="text-[9px] text-gray-400 mt-1 block">{{ resumenPagos.cantidadEfectivo || 0 }} pagos</span>
        </div>

        <!-- Transferencias & Zelle -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-purple-200 dark:border-purple-900/50">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-purple-600 dark:text-purple-400">Transf. / Zelle</span>
            <div class="p-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/30 text-purple-600">
              <Icon icon="mdi:bank-transfer" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">${{ formatMonto(resumenPagos.totalTransferenciaZelleUsd) }}</p>
          <p class="text-[10px] sm:text-[11px] font-bold text-purple-600 dark:text-purple-400 mt-0.5 font-mono">
            Bs. {{ formatBs(resumenPagos.totalTransferenciaZelleBs) }}
          </p>
          <span class="text-[9px] text-gray-400 mt-1 block">{{ resumenPagos.cantidadTransferenciaZelle || 0 }} operaciones</span>
        </div>

        <!-- Punto de Venta / Otros -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-indigo-200 dark:border-indigo-900/50">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400">Punto / Tarjeta</span>
            <div class="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600">
              <Icon icon="mdi:credit-card-outline" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-lg sm:text-2xl font-black text-gray-900 dark:text-white">${{ formatMonto(resumenPagos.totalPuntoVentaUsd) }}</p>
          <p class="text-[10px] sm:text-[11px] font-bold text-indigo-600 dark:text-indigo-400 mt-0.5 font-mono">
            Bs. {{ formatBs(resumenPagos.totalPuntoVentaBs) }}
          </p>
          <span class="text-[9px] text-gray-400 mt-1 block">{{ (resumenPagos.cantidadOtros || 0) }} registros</span>
        </div>
      </div>

      <!-- BARRA DE FILTROS PARA PAGOS -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 space-y-3">
        <!-- Presets rápidos de fecha -->
        <div class="flex items-center gap-1.5 flex-wrap">
          <span class="text-[10px] font-black uppercase text-gray-400 mr-1">Periodo:</span>
          <button
            v-for="p in presetsPeriodo"
            :key="p.key"
            @click="seleccionarPresetFecha(p.key, 'pagos')"
            :class="[
              'px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer',
              filtroPresetPagos === p.key
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
            ]"
          >
            {{ p.label }}
          </button>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-2.5 items-end">
          <!-- Fecha Desde -->
          <div class="lg:col-span-2">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Desde</label>
            <input
              type="date"
              v-model="filtrosPagos.fecha_desde"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <!-- Fecha Hasta -->
          <div class="lg:col-span-2">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Hasta</label>
            <input
              type="date"
              v-model="filtrosPagos.fecha_hasta"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <!-- Método de Pago -->
          <div class="lg:col-span-3">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Método de Pago</label>
            <select
              v-model="filtrosPagos.metodo_pago"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs font-bold text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500"
            >
              <option value="todos">⚡ Todos los métodos</option>
              <option value="cashea">🟡 Crédito Cashea</option>
              <option value="pago_movil">📱 Pago Móvil</option>
              <option value="efectivo">💵 Efectivo (USD / Bs)</option>
              <option value="transferencia">🏦 Transferencia Bancaria</option>
              <option value="zelle">🟣 Zelle</option>
              <option value="punto">💳 Punto de Venta / Tarjeta</option>
            </select>
          </div>

          <!-- Plan / Membresía -->
          <div class="lg:col-span-2">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Plan / Membresía</label>
            <select
              v-model="filtrosPagos.idTipoMembresia"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">Todos los planes</option>
              <option v-for="t in tiposMembresia" :key="t.idTipoMembresia" :value="t.idTipoMembresia">
                {{ t.nombre }}
              </option>
            </select>
          </div>

          <!-- Buscador por Socio / Documento -->
          <div class="lg:col-span-3">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Buscar Socio / Cédula</label>
            <div class="relative">
              <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="text"
                v-model="filtrosPagos.busqueda"
                placeholder="Nombre, cédula, recibo..."
                class="w-full pl-8 pr-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-800 dark:text-white focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
        </div>

        <!-- Botones de Acción de Filtro y Exportación -->
        <div class="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div class="flex items-center gap-2">
            <button
              @click="cargarPagos"
              class="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
              :disabled="cargandoPagos"
            >
              <Icon icon="mdi:filter" :class="{'animate-spin': cargandoPagos}" class="text-sm" />
              <span>{{ cargandoPagos ? 'Filtrando...' : 'Aplicar Filtros' }}</span>
            </button>
            <button
              @click="limpiarFiltrosPagos"
              class="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-xl text-xs font-bold flex items-center gap-1 transition-all cursor-pointer"
            >
              <Icon icon="mdi:filter-off" class="text-sm" />
              <span>Limpiar</span>
            </button>
          </div>

          <div class="flex items-center gap-2">
            <!-- Exportar a Excel (CSV) -->
            <button
              @click="exportarPagosExcel"
              class="px-3 py-2 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
              :disabled="pagosList.length === 0"
            >
              <Icon icon="mdi:file-excel" class="text-base text-green-600" />
              <span>Exportar Excel</span>
            </button>

            <!-- Imprimir Reporte -->
            <button
              @click="imprimirReporte('pagos')"
              class="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
              :disabled="pagosList.length === 0"
            >
              <Icon icon="mdi:printer" class="text-base text-indigo-600" />
              <span>Imprimir</span>
            </button>
          </div>
        </div>
      </div>

      <!-- TABLA DETALLADA DE PAGOS -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div class="p-3 sm:p-4 bg-gray-50/50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div class="flex items-center gap-2">
            <span class="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
            <h3 class="text-xs sm:text-sm font-black text-gray-800 dark:text-white uppercase tracking-wider">
              Listado de Pagos de Gimnasio ({{ pagosList.length }})
            </h3>
          </div>
          <span class="text-[11px] text-gray-500 font-bold">
            Total Mostrado: <strong class="text-emerald-600 dark:text-emerald-400">${{ formatMonto(totalPagosFiltradosUsd) }}</strong>
          </span>
        </div>

        <div class="overflow-x-auto custom-scrollbar">
          <table class="w-full text-left text-xs">
            <thead>
              <tr class="bg-gray-100/60 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] font-black">
                <th class="px-3 py-3">Fecha / Hora</th>
                <th class="px-3 py-3">Socio / Cliente</th>
                <th class="px-3 py-3">Documento</th>
                <th class="px-3 py-3">Plan / Concepto</th>
                <th class="px-3 py-3">Método de Pago</th>
                <th class="px-3 py-3">Referencia / Recibo</th>
                <th class="px-3 py-3 text-right">Monto USD</th>
                <th class="px-3 py-3 text-right">Monto Bs</th>
                <th class="px-3 py-3 text-center">Estado</th>
                <th class="px-3 py-3">Cobrado Por</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
              <tr v-if="cargandoPagos">
                <td colspan="10" class="px-3 py-10 text-center text-gray-400">
                  <Icon icon="mdi:loading" class="animate-spin text-3xl mx-auto mb-2 text-emerald-600" />
                  <p class="font-bold">Cargando reporte de pagos...</p>
                </td>
              </tr>
              <tr v-else-if="pagosList.length === 0">
                <td colspan="10" class="px-3 py-12 text-center text-gray-400">
                  <Icon icon="mdi:cash-remove" class="text-4xl mx-auto mb-2 opacity-50" />
                  <p class="font-bold text-gray-600 dark:text-gray-300">No se encontraron pagos con los filtros seleccionados</p>
                  <p class="text-[11px] text-gray-400 mt-1">Prueba seleccionando otro rango de fechas o método de pago</p>
                </td>
              </tr>
              <tr
                v-else
                v-for="pago in pagosList"
                :key="pago.idPago + '_' + (pago.numeroRecibo || Math.random())"
                class="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors"
                :class="{'bg-yellow-50/30 dark:bg-yellow-900/10': pago.esCashea}"
              >
                <!-- Fecha -->
                <td class="px-3 py-3 font-mono text-[11px] text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {{ formatFechaHora(pago.fechaPago) }}
                </td>

                <!-- Socio -->
                <td class="px-3 py-3">
                  <p class="font-black text-gray-900 dark:text-white">{{ pago.socioNombre }}</p>
                  <p v-if="pago.socioTelefono" class="text-[10px] text-gray-400 font-mono">{{ pago.socioTelefono }}</p>
                </td>

                <!-- Documento -->
                <td class="px-3 py-3 font-mono font-bold text-gray-700 dark:text-gray-300">
                  {{ pago.socioDocumento || 'S/D' }}
                </td>

                <!-- Plan -->
                <td class="px-3 py-3">
                  <span class="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 font-bold text-gray-700 dark:text-gray-300 text-[11px]">
                    {{ pago.tipoMembresia }}
                  </span>
                  <span v-if="pago.numeroMembresia" class="block text-[10px] text-gray-400 font-mono mt-0.5">
                    N°: {{ pago.numeroMembresia }}
                  </span>
                </td>

                <!-- Método de Pago (BADGE DISTINTIVO) -->
                <td class="px-3 py-3">
                  <!-- Cashea -->
                  <span
                    v-if="pago.esCashea"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-yellow-400 text-yellow-950 border border-yellow-500 shadow-xs"
                  >
                    <Icon icon="mdi:flash" class="text-xs" />
                    Crédito Cashea
                    <span v-if="pago.cuotaNumero" class="ml-0.5 bg-yellow-950 text-yellow-400 px-1 rounded text-[9px]">
                      {{ pago.cuotaNumero }}/{{ pago.cuotasTotales || 3 }}
                    </span>
                  </span>

                  <!-- Pago Móvil -->
                  <span
                    v-else-if="pago.esPagoMovil"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-300 dark:border-blue-700"
                  >
                    <Icon icon="mdi:cellphone-wireless" class="text-xs" />
                    Pago Móvil
                  </span>

                  <!-- Efectivo -->
                  <span
                    v-else-if="pago.metodoPago.toLowerCase().includes('efectivo')"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700"
                  >
                    <Icon icon="mdi:cash" class="text-xs" />
                    Efectivo
                  </span>

                  <!-- Zelle -->
                  <span
                    v-else-if="pago.metodoPago.toLowerCase().includes('zelle')"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 border border-purple-300 dark:border-purple-700"
                  >
                    <Icon icon="mdi:alpha-z-circle" class="text-xs" />
                    Zelle
                  </span>

                  <!-- Transferencia -->
                  <span
                    v-else-if="pago.metodoPago.toLowerCase().includes('transfer')"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300 border border-cyan-300 dark:border-cyan-700"
                  >
                    <Icon icon="mdi:bank" class="text-xs" />
                    Transferencia
                  </span>

                  <!-- Punto de Venta -->
                  <span
                    v-else-if="pago.metodoPago.toLowerCase().includes('punto') || pago.metodoPago.toLowerCase().includes('tarjeta')"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700"
                  >
                    <Icon icon="mdi:credit-card" class="text-xs" />
                    Punto de Venta
                  </span>

                  <!-- Genérico -->
                  <span
                    v-else
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                  >
                    {{ pago.metodoPago }}
                  </span>
                </td>

                <!-- Referencia -->
                <td class="px-3 py-3 font-mono text-[11px] text-gray-600 dark:text-gray-300">
                  {{ pago.referencia || pago.numeroRecibo || '—' }}
                </td>

                <!-- Monto USD -->
                <td class="px-3 py-3 text-right font-black text-gray-900 dark:text-white whitespace-nowrap">
                  ${{ formatMonto(pago.montoUsd) }}
                </td>

                <!-- Monto Bs -->
                <td class="px-3 py-3 text-right font-bold text-emerald-600 dark:text-emerald-400 font-mono whitespace-nowrap">
                  Bs. {{ formatBs(pago.montoBs) }}
                </td>

                <!-- Estado -->
                <td class="px-3 py-3 text-center whitespace-nowrap">
                  <span
                    class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider"
                    :class="[
                      pago.estado === 'Pagado' || pago.estado === 'Completado'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : pago.estado === 'Abono'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    ]"
                  >
                    {{ pago.estado || 'Pagado' }}
                  </span>
                </td>

                <!-- Cobrado Por -->
                <td class="px-3 py-3 text-gray-600 dark:text-gray-400 text-[11px]">
                  {{ pago.usuarioCajero || 'Caja' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ==================================================================== -->
    <!-- TAB 2: REPORTE DE ACCESOS Y ASISTENCIAS DEL GIMNASIO                  -->
    <!-- ==================================================================== -->
    <div v-show="tabActiva === 'accesos'" class="space-y-5 animate-fadeIn">
      <!-- TARJETAS KPI DE ASISTENCIAS -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4">
        <!-- Total Registros -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] font-black uppercase tracking-wider text-gray-400">Total Intentos</span>
            <div class="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              <Icon icon="mdi:format-list-numbered" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white">{{ totalAccesosRegistrados }}</p>
          <span class="text-[10px] text-gray-400 mt-1 block">Registros en el periodo</span>
        </div>

        <!-- Accesos Permitidos -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-emerald-200 dark:border-emerald-900/50">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] font-black uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Permitidos</span>
            <div class="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600">
              <Icon icon="mdi:check-circle" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400">{{ totalAccesosPermitidos }}</p>
          <span class="text-[10px] text-emerald-600/80 font-bold mt-1 block">Torniquete habilitado</span>
        </div>

        <!-- Accesos Denegados -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-red-200 dark:border-red-900/50">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] font-black uppercase tracking-wider text-red-600 dark:text-red-400">Denegados / Vencidos</span>
            <div class="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600">
              <Icon icon="mdi:close-circle" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-xl sm:text-3xl font-black text-red-600 dark:text-red-400">{{ totalAccesosDenegados }}</p>
          <span class="text-[10px] text-red-600/80 font-bold mt-1 block">Bloqueados o vencidos</span>
        </div>

        <!-- % Efectividad / Aprobación -->
        <div class="bg-white dark:bg-gray-800 rounded-2xl p-3 sm:p-4 shadow-sm border border-cyan-200 dark:border-cyan-900/50">
          <div class="flex items-center justify-between mb-1.5">
            <span class="text-[10px] font-black uppercase tracking-wider text-cyan-600 dark:text-cyan-400">Tasa de Acceso</span>
            <div class="p-1.5 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600">
              <Icon icon="mdi:percent" class="text-base sm:text-lg" />
            </div>
          </div>
          <p class="text-xl sm:text-3xl font-black text-cyan-600 dark:text-cyan-400">{{ tasaAprobacion }}%</p>
          <span class="text-[10px] text-gray-400 mt-1 block">Porcentaje autorizado</span>
        </div>
      </div>

      <!-- BARRA DE FILTROS PARA ACCESOS -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 space-y-3">
        <!-- Presets rápidos -->
        <div class="flex items-center gap-1.5 flex-wrap">
          <span class="text-[10px] font-black uppercase text-gray-400 mr-1">Periodo:</span>
          <button
            v-for="p in presetsPeriodo"
            :key="p.key"
            @click="seleccionarPresetFecha(p.key, 'accesos')"
            :class="[
              'px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer',
              filtroPresetAccesos === p.key
                ? 'bg-cyan-600 text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
            ]"
          >
            {{ p.label }}
          </button>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-2.5 items-end">
          <!-- Fecha Desde -->
          <div class="lg:col-span-2">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Desde</label>
            <input
              type="date"
              v-model="filtrosAccesos.fecha_desde"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-800 dark:text-white focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <!-- Fecha Hasta -->
          <div class="lg:col-span-2">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Hasta</label>
            <input
              type="date"
              v-model="filtrosAccesos.fecha_hasta"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-800 dark:text-white focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <!-- Resultado -->
          <div class="lg:col-span-2">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Resultado</label>
            <select
              v-model="filtrosAccesos.resultado"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs font-bold text-gray-800 dark:text-white focus:ring-2 focus:ring-cyan-500"
            >
              <option value="todos">Todos los resultados</option>
              <option value="permitido">✅ Solo Permitidos</option>
              <option value="denegado">❌ Denegados / Sin Membresía</option>
              <option value="expirado">⏳ Expirados / Vencidos</option>
              <option value="fuera_horario">🕒 Fuera de Horario</option>
            </select>
          </div>

          <!-- Método de Ingreso -->
          <div class="lg:col-span-2">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Método de Ingreso</label>
            <select
              v-model="filtrosAccesos.tipo_metodo"
              class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-800 dark:text-white focus:ring-2 focus:ring-cyan-500"
            >
              <option value="todos">Todos los métodos</option>
              <option value="huella">🖐️ Lector Huella (FS88H)</option>
              <option value="cedula">💳 Cédula / Manual</option>
              <option value="facial">🎭 Reconocimiento Facial</option>
              <option value="manual">🚪 Apertura Manual</option>
            </select>
          </div>

          <!-- Buscador por Socio / Documento -->
          <div class="lg:col-span-4">
            <label class="block text-[10px] font-black text-gray-400 uppercase mb-1">Buscar Socio / Cédula / Motivo</label>
            <div class="relative">
              <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base" />
              <input
                type="text"
                v-model="filtrosAccesos.busqueda"
                placeholder="Nombre, cédula, motivo..."
                class="w-full pl-8 pr-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-800 dark:text-white focus:ring-2 focus:ring-cyan-500"
              />
            </div>
          </div>
        </div>

        <!-- Botones de Acción de Filtro y Exportación -->
        <div class="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <div class="flex items-center gap-2">
            <button
              @click="cargarAccesos"
              class="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-xs font-black shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
              :disabled="cargandoAccesos"
            >
              <Icon icon="mdi:filter" :class="{'animate-spin': cargandoAccesos}" class="text-sm" />
              <span>{{ cargandoAccesos ? 'Consultando...' : 'Aplicar Filtros' }}</span>
            </button>
            <button
              @click="limpiarFiltrosAccesos"
              class="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-xl text-xs font-bold flex items-center gap-1 transition-all cursor-pointer"
            >
              <Icon icon="mdi:filter-off" class="text-sm" />
              <span>Limpiar</span>
            </button>
          </div>

          <div class="flex items-center gap-2">
            <!-- Exportar a Excel (CSV) -->
            <button
              @click="exportarAccesosExcel"
              class="px-3 py-2 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
              :disabled="accesosList.length === 0"
            >
              <Icon icon="mdi:file-excel" class="text-base text-green-600" />
              <span>Exportar Excel</span>
            </button>

            <!-- Imprimir Reporte -->
            <button
              @click="imprimirReporte('accesos')"
              class="px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer shadow-sm"
              :disabled="accesosList.length === 0"
            >
              <Icon icon="mdi:printer" class="text-base text-indigo-600" />
              <span>Imprimir</span>
            </button>
          </div>
        </div>
      </div>

      <!-- TABLA DETALLADA DE ACCESOS -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div class="p-3 sm:p-4 bg-gray-50/50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div class="flex items-center gap-2">
            <span class="w-2.5 h-2.5 rounded-full bg-cyan-500"></span>
            <h3 class="text-xs sm:text-sm font-black text-gray-800 dark:text-white uppercase tracking-wider">
              Historial de Accesos al Gimnasio ({{ accesosList.length }})
            </h3>
          </div>
          <span class="text-[11px] text-gray-500 font-bold">
            Permitidos: <strong class="text-emerald-600 dark:text-emerald-400">{{ totalAccesosPermitidos }}</strong> |
            Denegados: <strong class="text-red-600 dark:text-red-400">{{ totalAccesosDenegados }}</strong>
          </span>
        </div>

        <div class="overflow-x-auto custom-scrollbar">
          <table class="w-full text-left text-xs">
            <thead>
              <tr class="bg-gray-100/60 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] font-black">
                <th class="px-3 py-3">Fecha / Hora</th>
                <th class="px-3 py-3">Socio / Persona</th>
                <th class="px-3 py-3">Cédula / Documento</th>
                <th class="px-3 py-3">Plan / Membresía</th>
                <th class="px-3 py-3 text-center">Método</th>
                <th class="px-3 py-3 text-center">Resultado</th>
                <th class="px-3 py-3">Motivo / Detalle</th>
                <th class="px-3 py-3 text-center">Vigencia</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60">
              <tr v-if="cargandoAccesos">
                <td colspan="8" class="px-3 py-10 text-center text-gray-400">
                  <Icon icon="mdi:loading" class="animate-spin text-3xl mx-auto mb-2 text-cyan-600" />
                  <p class="font-bold">Cargando registros de accesos...</p>
                </td>
              </tr>
              <tr v-else-if="accesosList.length === 0">
                <td colspan="8" class="px-3 py-12 text-center text-gray-400">
                  <Icon icon="mdi:door-closed" class="text-4xl mx-auto mb-2 opacity-50" />
                  <p class="font-bold text-gray-600 dark:text-gray-300">No se encontraron accesos registrados en este periodo</p>
                  <p class="text-[11px] text-gray-400 mt-1">Modifica el rango de fechas para ver el historial anterior</p>
                </td>
              </tr>
              <tr
                v-else
                v-for="acceso in accesosList"
                :key="acceso.idAcceso"
                class="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors"
                :class="acceso.resultado === 'permitido' ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500'"
              >
                <!-- Fecha y Hora -->
                <td class="px-3 py-3 font-mono text-[11px] text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {{ formatFechaHora(acceso.fecha_hora || acceso.fechaHora) }}
                </td>

                <!-- Socio con Foto -->
                <td class="px-3 py-3">
                  <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 border border-gray-200 dark:border-gray-600 flex items-center justify-center">
                      <img
                        v-if="acceso.foto_socio || acceso.foto_perfil_usuario"
                        :src="getFotoUrl(acceso.foto_socio || acceso.foto_perfil_usuario)"
                        class="w-full h-full object-cover"
                        @error="(e) => e.target.style.display = 'none'"
                      />
                      <Icon v-else icon="mdi:account" class="text-gray-400 text-lg" />
                    </div>
                    <div>
                      <p class="font-black text-gray-900 dark:text-white leading-tight">
                        {{ acceso.nombre_socio || 'Desconocido' }}
                      </p>
                      <span v-if="acceso.valor_ingresado && !acceso.nombre_socio" class="text-[10px] text-gray-400 font-mono">
                        {{ acceso.valor_ingresado }}
                      </span>
                    </div>
                  </div>
                </td>

                <!-- Documento -->
                <td class="px-3 py-3 font-mono font-bold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {{ acceso.documento_socio || acceso.valor_ingresado || '—' }}
                </td>

                <!-- Plan -->
                <td class="px-3 py-3">
                  <span class="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 font-bold text-gray-700 dark:text-gray-300 text-[11px]">
                    {{ acceso.tipo_membresia || 'Sin plan' }}
                  </span>
                </td>

                <!-- Método -->
                <td class="px-3 py-3 text-center whitespace-nowrap">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                    :class="[
                      acceso.tipo_metodo === 'huella'
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                        : acceso.tipo_metodo === 'facial'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    ]"
                  >
                    <Icon :icon="getIconoMetodo(acceso.tipo_metodo)" />
                    {{ getMetodoLabel(acceso.tipo_metodo) }}
                  </span>
                </td>

                <!-- Resultado -->
                <td class="px-3 py-3 text-center whitespace-nowrap">
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider"
                    :class="[
                      acceso.resultado === 'permitido'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    ]"
                  >
                    <Icon :icon="acceso.resultado === 'permitido' ? 'mdi:check-circle' : 'mdi:close-circle'" />
                    {{ acceso.resultado === 'permitido' ? 'Permitido' : 'Denegado' }}
                  </span>
                </td>

                <!-- Motivo Denegación -->
                <td class="px-3 py-3">
                  <p
                    v-if="acceso.motivo_denegacion"
                    class="text-red-600 dark:text-red-400 font-semibold text-[11px] truncate max-w-[220px]"
                    :title="acceso.motivo_denegacion"
                  >
                    {{ acceso.motivo_denegacion }}
                  </p>
                  <span v-else class="text-emerald-600 dark:text-emerald-400 font-bold text-[10px]">
                    Ingreso Autorizado
                  </span>
                </td>

                <!-- Días restantes / Vigencia -->
                <td class="px-3 py-3 text-center whitespace-nowrap">
                  <span
                    v-if="acceso.dias_restantes !== undefined && acceso.dias_restantes !== null"
                    :class="[
                      'px-2 py-0.5 rounded font-black text-[10px]',
                      acceso.dias_restantes > 7
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : acceso.dias_restantes > 0
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    ]"
                  >
                    {{ acceso.dias_restantes > 0 ? (acceso.dias_restantes + ' días') : (acceso.dias_restantes === 0 ? 'Vence Hoy' : 'Vencido') }}
                  </span>
                  <span v-else class="text-gray-400 text-[10px]">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';
import { obtenerReportePagosGym, obtenerReporteAccesosGym } from '@/api/gymReportes';
import { obtenerTiposMembresia } from '@/api/membresias';
import { getCurrentApiUrl } from '@/api/index.js';

const toast = useToast();

// Control de pestaña activa
const tabActiva = ref('pagos'); // 'pagos' | 'accesos'

// Tipos de membresía disponibles para filtros
const tiposMembresia = ref([]);

// Presets de periodos
const presetsPeriodo = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'ayer', label: 'Ayer' },
  { key: 'semana', label: 'Esta Semana' },
  { key: 'mes', label: 'Este Mes' },
  { key: 'mes_anterior', label: 'Mes Anterior' },
  { key: 'todo', label: 'Histórico' }
];

// Fechas helpers (YYYY-MM-DD en hora local)
const getTodayStr = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

const getFirstDayOfMonth = () => {
  const d = new Date();
  const f = new Date(d.getFullYear(), d.getMonth(), 1);
  return f.toISOString().split('T')[0];
};

// ============================================================
// ESTADOS TAB 1: PAGOS
// ============================================================
const cargandoPagos = ref(false);
const pagosList = ref([]);
const resumenPagos = ref({
  totalRecaudadoUsd: 0,
  totalRecaudadoBs: 0,
  totalCasheaUsd: 0,
  totalCasheaBs: 0,
  totalPagoMovilUsd: 0,
  totalPagoMovilBs: 0,
  totalEfectivoUsd: 0,
  totalEfectivoBs: 0,
  totalTransferenciaZelleUsd: 0,
  totalTransferenciaZelleBs: 0,
  totalPuntoVentaUsd: 0,
  totalPuntoVentaBs: 0,
  totalOtrosUsd: 0,
  totalOtrosBs: 0,
  totalTransacciones: 0,
  cantidadCashea: 0,
  cantidadPagoMovil: 0,
  cantidadEfectivo: 0,
  cantidadTransferenciaZelle: 0,
  cantidadOtros: 0
});

const filtroPresetPagos = ref('mes');
const filtrosPagos = ref({
  fecha_desde: getFirstDayOfMonth(),
  fecha_hasta: getTodayStr(),
  metodo_pago: 'todos',
  idTipoMembresia: '',
  busqueda: ''
});

const totalPagosFiltradosUsd = computed(() => {
  return pagosList.value.reduce((acc, p) => acc + (parseFloat(p.montoUsd) || 0), 0);
});

// ============================================================
// ESTADOS TAB 2: ACCESOS
// ============================================================
const cargandoAccesos = ref(false);
const accesosList = ref([]);
const filtroPresetAccesos = ref('hoy');
const filtrosAccesos = ref({
  fecha_desde: getTodayStr(),
  fecha_hasta: getTodayStr(),
  resultado: 'todos',
  tipo_metodo: 'todos',
  busqueda: ''
});

const totalAccesosRegistrados = computed(() => accesosList.value.length);
const totalAccesosPermitidos = computed(() => accesosList.value.filter(a => a.resultado === 'permitido').length);
const totalAccesosDenegados = computed(() => accesosList.value.filter(a => a.resultado !== 'permitido').length);
const tasaAprobacion = computed(() => {
  if (totalAccesosRegistrados.value === 0) return 0;
  return Math.round((totalAccesosPermitidos.value / totalAccesosRegistrados.value) * 100);
});

// ============================================================
// MANEJO DE PRESETS DE FECHAS
// ============================================================
const seleccionarPresetFecha = (key, tipo) => {
  const hoy = new Date();
  let desde = '';
  let hasta = hoy.toISOString().split('T')[0];

  if (key === 'hoy') {
    desde = hasta;
  } else if (key === 'ayer') {
    const ayer = new Date();
    ayer.setDate(ayer.getDate() - 1);
    desde = ayer.toISOString().split('T')[0];
    hasta = desde;
  } else if (key === 'semana') {
    const d = new Date();
    const diaSemana = d.getDay() || 7; // 1 = Lunes
    d.setDate(d.getDate() - diaSemana + 1);
    desde = d.toISOString().split('T')[0];
  } else if (key === 'mes') {
    desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().split('T')[0];
  } else if (key === 'mes_anterior') {
    const inicioAnt = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    const finAnt = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
    desde = inicioAnt.toISOString().split('T')[0];
    hasta = finAnt.toISOString().split('T')[0];
  } else if (key === 'todo') {
    desde = '';
    hasta = '';
  }

  if (tipo === 'pagos') {
    filtroPresetPagos.value = key;
    filtrosPagos.value.fecha_desde = desde;
    filtrosPagos.value.fecha_hasta = hasta;
    cargarPagos();
  } else {
    filtroPresetAccesos.value = key;
    filtrosAccesos.value.fecha_desde = desde;
    filtrosAccesos.value.fecha_hasta = hasta;
    cargarAccesos();
  }
};

// ============================================================
// CARGA DE DATOS
// ============================================================
const cargarTipos = async () => {
  try {
    const res = await obtenerTiposMembresia();
    if (res.success && res.data) {
      tiposMembresia.value = res.data;
    }
  } catch (e) {
    console.warn('Error cargando tipos:', e);
  }
};

const cargarPagos = async () => {
  cargandoPagos.value = true;
  try {
    const res = await obtenerReportePagosGym(filtrosPagos.value);
    if (res.success) {
      pagosList.value = res.pagos || [];
      resumenPagos.value = res.resumen || {};
    } else {
      toast.error('Error al cargar reporte de pagos: ' + (res.error || ''));
    }
  } catch (error) {
    toast.error('Error de conexión al cargar pagos');
  } finally {
    cargandoPagos.value = false;
  }
};

const limpiarFiltrosPagos = () => {
  filtroPresetPagos.value = 'mes';
  filtrosPagos.value = {
    fecha_desde: getFirstDayOfMonth(),
    fecha_hasta: getTodayStr(),
    metodo_pago: 'todos',
    idTipoMembresia: '',
    busqueda: ''
  };
  cargarPagos();
};

const cargarAccesos = async () => {
  cargandoAccesos.value = true;
  try {
    const res = await obtenerReporteAccesosGym(filtrosAccesos.value);
    if (res.success) {
      accesosList.value = res.data || [];
    } else {
      toast.error('Error al consultar historial de accesos: ' + (res.error || ''));
    }
  } catch (error) {
    toast.error('Error de conexión al cargar accesos');
  } finally {
    cargandoAccesos.value = false;
  }
};

const limpiarFiltrosAccesos = () => {
  filtroPresetAccesos.value = 'hoy';
  filtrosAccesos.value = {
    fecha_desde: getTodayStr(),
    fecha_hasta: getTodayStr(),
    resultado: 'todos',
    tipo_metodo: 'todos',
    busqueda: ''
  };
  cargarAccesos();
};

// ============================================================
// EXPORTACIÓN A EXCEL (CSV)
// ============================================================
const exportarPagosExcel = () => {
  if (pagosList.value.length === 0) {
    toast.warning('No hay datos para exportar');
    return;
  }

  const cabeceras = [
    'ID Pago',
    'Fecha',
    'Socio',
    'Documento',
    'Telefono',
    'Plan',
    'Metodo de Pago',
    'Es Cashea',
    'Es Pago Movil',
    'Monto USD',
    'Monto Bs',
    'Tasa Cambio',
    'Referencia',
    'Estado',
    'Cuota',
    'Cajero'
  ];

  const filas = pagosList.value.map(p => [
    p.idPago,
    `"${formatFechaHora(p.fechaPago)}"`,
    `"${p.socioNombre || ''}"`,
    `"${p.socioDocumento || ''}"`,
    `"${p.socioTelefono || ''}"`,
    `"${p.tipoMembresia || ''}"`,
    `"${p.metodoPago || ''}"`,
    p.esCashea ? 'SI' : 'NO',
    p.esPagoMovil ? 'SI' : 'NO',
    p.montoUsd,
    p.montoBs,
    p.tasaCambio,
    `"${p.referencia || p.numeroRecibo || ''}"`,
    `"${p.estado || ''}"`,
    p.cuotaNumero ? `"${p.cuotaNumero}/${p.cuotasTotales || 3}"` : '""',
    `"${p.usuarioCajero || ''}"`
  ]);

  const csvContent = '\uFEFF' + [cabeceras.join(';'), ...filas.map(f => f.join(';'))].join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `reporte_pagos_gym_${getTodayStr()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.success('Archivo Excel (CSV) generado con éxito');
};

const exportarAccesosExcel = () => {
  if (accesosList.value.length === 0) {
    toast.warning('No hay datos para exportar');
    return;
  }

  const cabeceras = [
    'ID Acceso',
    'Fecha y Hora',
    'Socio',
    'Documento',
    'Plan',
    'Metodo Ingreso',
    'Resultado',
    'Motivo Denegacion',
    'Dias Restantes'
  ];

  const filas = accesosList.value.map(a => [
    a.idAcceso,
    `"${formatFechaHora(a.fecha_hora || a.fechaHora)}"`,
    `"${a.nombre_socio || 'Desconocido'}"`,
    `"${a.documento_socio || a.valor_ingresado || ''}"`,
    `"${a.tipo_membresia || ''}"`,
    `"${a.tipo_metodo || ''}"`,
    `"${a.resultado || ''}"`,
    `"${a.motivo_denegacion || ''}"`,
    a.dias_restantes ?? ''
  ]);

  const csvContent = '\uFEFF' + [cabeceras.join(';'), ...filas.map(f => f.join(';'))].join('\r\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `reporte_accesos_gym_${getTodayStr()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.success('Archivo Excel (CSV) de accesos generado con éxito');
};

// ============================================================
// IMPRESIÓN PROFESIONAL
// ============================================================
const imprimirReporte = (tipo) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Permita las ventanas emergentes para imprimir');
    return;
  }

  let titulo = '';
  let contenidoTabla = '';
  let resumenHtml = '';

  if (tipo === 'pagos') {
    titulo = 'REPORTE FINANCIERO Y DE PAGOS DEL GIMNASIO';
    resumenHtml = `
      <div style="display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 12px; background: #f8fafc; padding: 12px; border-radius: 8px;">
        <div><strong>Total Recaudado USD:</strong> $${formatMonto(resumenPagos.value.totalRecaudadoUsd)}</div>
        <div><strong>Total Recaudado Bs:</strong> Bs. ${formatBs(resumenPagos.value.totalRecaudadoBs)}</div>
        <div><strong>Crédito Cashea:</strong> $${formatMonto(resumenPagos.value.totalCasheaUsd)} (${resumenPagos.value.cantidadCashea || 0})</div>
        <div><strong>Pago Móvil:</strong> $${formatMonto(resumenPagos.value.totalPagoMovilUsd)} (${resumenPagos.value.cantidadPagoMovil || 0})</div>
        <div><strong>Efectivo:</strong> $${formatMonto(resumenPagos.value.totalEfectivoUsd)}</div>
      </div>
    `;

    const rows = pagosList.value.map(p => `
      <tr>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; font-family: monospace;">${formatFechaHora(p.fechaPago)}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; font-weight: bold;">${p.socioNombre || ''}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; font-family: monospace;">${p.socioDocumento || ''}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0;">${p.tipoMembresia || ''}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; font-weight: bold; color: ${p.esCashea ? '#b45309' : (p.esPagoMovil ? '#1d4ed8' : '#047857')}">${p.metodoPago}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; font-family: monospace;">${p.referencia || p.numeroRecibo || '-'}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: bold;">$${formatMonto(p.montoUsd)}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; text-align: right;">Bs. ${formatBs(p.montoBs)}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; text-align: center;">${p.estado}</td>
      </tr>
    `).join('');

    contenidoTabla = `
      <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
        <thead>
          <tr style="background: #e2e8f0; text-align: left;">
            <th style="padding: 8px;">Fecha</th>
            <th style="padding: 8px;">Socio</th>
            <th style="padding: 8px;">Documento</th>
            <th style="padding: 8px;">Plan</th>
            <th style="padding: 8px;">Método</th>
            <th style="padding: 8px;">Referencia</th>
            <th style="padding: 8px; text-align: right;">USD</th>
            <th style="padding: 8px; text-align: right;">Bs.</th>
            <th style="padding: 8px; text-align: center;">Estado</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  } else {
    titulo = 'REPORTE DE ASISTENCIA Y ACCESOS AL GIMNASIO';
    resumenHtml = `
      <div style="display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 12px; background: #f8fafc; padding: 12px; border-radius: 8px;">
        <div><strong>Total Intentos:</strong> ${totalAccesosRegistrados.value}</div>
        <div><strong>Permitidos:</strong> ${totalAccesosPermitidos.value}</div>
        <div><strong>Denegados:</strong> ${totalAccesosDenegados.value}</div>
        <div><strong>Tasa Aprobación:</strong> ${tasaAprobacion.value}%</div>
      </div>
    `;

    const rows = accesosList.value.map(a => `
      <tr>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; font-family: monospace;">${formatFechaHora(a.fecha_hora || a.fechaHora)}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; font-weight: bold;">${a.nombre_socio || 'Desconocido'}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; font-family: monospace;">${a.documento_socio || a.valor_ingresado || '-'}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0;">${a.tipo_membresia || 'Sin plan'}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; text-align: center;">${getMetodoLabel(a.tipo_metodo)}</td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; text-align: center; font-weight: bold; color: ${a.resultado === 'permitido' ? '#047857' : '#b91c1c'}">
          ${a.resultado === 'permitido' ? 'PERMITIDO' : 'DENEGADO'}
        </td>
        <td style="padding: 6px; border-bottom: 1px solid #e2e8f0; color: #b91c1c;">${a.motivo_denegacion || 'Acceso Autorizado'}</td>
      </tr>
    `).join('');

    contenidoTabla = `
      <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
        <thead>
          <tr style="background: #e2e8f0; text-align: left;">
            <th style="padding: 8px;">Fecha/Hora</th>
            <th style="padding: 8px;">Socio</th>
            <th style="padding: 8px;">Documento</th>
            <th style="padding: 8px;">Plan</th>
            <th style="padding: 8px; text-align: center;">Método</th>
            <th style="padding: 8px; text-align: center;">Resultado</th>
            <th style="padding: 8px;">Motivo</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${titulo}</title>
        <style>
          body { font-family: system-ui, sans-serif; padding: 20px; color: #1e293b; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0f766e; padding-bottom: 15px; }
          .title { font-size: 18px; font-weight: 900; margin: 0; color: #0f766e; }
          .subtitle { font-size: 12px; color: #64748b; margin-top: 5px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="title">${titulo}</h1>
          <p class="subtitle">SYSTEM JJ C.A. - Sistema de Gestión de Gimnasio · Fecha de Impresión: ${new Date().toLocaleString('es-VE')}</p>
        </div>
        ${resumenHtml}
        ${contenidoTabla}
        <script>
          window.onload = function() { window.print(); };
        <\/script>
      </body>
    </html>
  `;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
};

// ============================================================
// FORMATTERS & HELPERS
// ============================================================
const formatMonto = (v) => {
  const num = parseFloat(v);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatBs = (v) => {
  const num = parseFloat(v);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatFechaHora = (fechaStr) => {
  if (!fechaStr) return '—';
  try {
    const f = new Date(fechaStr);
    return f.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
           f.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return fechaStr;
  }
};

const getMetodoLabel = (m) => {
  if (m === 'huella') return 'Huella FS88H';
  if (m === 'facial') return 'Facial FSO701';
  if (m === 'cedula') return 'Cédula / Manual';
  if (m === 'manual') return 'Torniquete';
  return m || 'Manual';
};

const getIconoMetodo = (m) => {
  if (m === 'huella') return 'mdi:fingerprint';
  if (m === 'facial') return 'mdi:face-recognition';
  if (m === 'cedula') return 'mdi:card-account-details';
  return 'mdi:gate';
};

const getFotoUrl = (foto) => {
  if (!foto) return '';
  if (foto.startsWith('data:') || foto.startsWith('http')) return foto;
  return `${getCurrentApiUrl()}${foto}`;
};

// Inicialización
onMounted(async () => {
  await cargarTipos();
  await cargarPagos();
  await cargarAccesos();
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  height: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.4);
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.7);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fadeIn {
  animation: fadeIn 0.25s ease-out forwards;
}
</style>

